import { Box, Flex, HStack, IconButton, VStack } from '@chakra-ui/react';
import Checkbox from 'design-system/molecules/checkbox';
import { FormattedMessage } from 'react-intl';
import { SettingsIcon } from '@chakra-ui/icons';
import ContextMenu from 'design-system/molecules/context-menu';
import { useState, useEffect } from 'react';
import { WizardModal } from 'design-system/molecules/modal';
import AlertBox from 'design-system/atoms/alert-box';
import { SegmentBusRoute } from '../../data/useSearchPublicBusData';
import BusStopsRouteEditor from '../BusStopsRouteEditor';

export default function StepSelectRoutes({
  busRoutes,
  routeColors,
  excludedRoutes,
  onSelectedRoutesChange,
  stopsOverride,
  onStopsOverrideChange,
  onRouteHover,
  highlightedRoute,
  onHasSingleStopChange,
}: {
  busRoutes: SegmentBusRoute[];
  routeColors: Record<string, string>;
  excludedRoutes: string[];
  onSelectedRoutesChange: (excludedRoutes: string[]) => void;
  stopsOverride: Record<string, { firstStopId?: string; lastStopId?: string }>;
  onStopsOverrideChange: (
    overrideStops: Record<string, { firstStopId?: string; lastStopId?: string }>
  ) => void;
  onRouteHover: (routeId: string | undefined) => void;
  highlightedRoute: string | undefined;
  onHasSingleStopChange: (hasSingleStop: boolean) => void;
}) {
  const [editingRouteId, setEditingRouteId] = useState<string | undefined>(
    undefined
  );
  const editingRouteOriginalProps = busRoutes.find(
    (route) => route.routeId === editingRouteId
  );
  const editingRoute =
    editingRouteId && editingRouteOriginalProps
      ? {
          ...editingRouteOriginalProps,
          ...stopsOverride[editingRouteId],
        }
      : undefined;
  const onRouteStopsChange = (busRoute: SegmentBusRoute) => {
    const overrideProps: { firstStopId?: string; lastStopId?: string } = {};
    if (busRoute.firstStopId !== editingRouteOriginalProps?.firstStopId) {
      overrideProps.firstStopId = busRoute.firstStopId;
    }
    if (busRoute.lastStopId !== editingRouteOriginalProps?.lastStopId) {
      overrideProps.lastStopId = busRoute.lastStopId;
    }
    onStopsOverrideChange({
      ...stopsOverride,
      [busRoute.routeId]: overrideProps,
    });
  };
  const hasSingleStop = busRoutes.some((route) => {
    if (excludedRoutes.includes(route.routeId)) return false;
    const firstStopId =
      stopsOverride[route.routeId]?.firstStopId ?? route.firstStopId;
    const lastStopId =
      stopsOverride[route.routeId]?.lastStopId ?? route.lastStopId;
    return firstStopId === lastStopId;
  });

  useEffect(() => {
    onHasSingleStopChange(hasSingleStop);
  }, [hasSingleStop, onHasSingleStopChange]);
  return (
    <>
      <VStack width="100%" spacing={4} alignItems="stretch">
        <Box fontSize="md" color="gray.500">
          <FormattedMessage
            defaultMessage="Select routes and adjust the start and stop locations if required"
            id="qC86sZ"
          />
        </Box>
        {Object.entries(
          busRoutes.reduce(
            (acc, busRoute) => {
              const group = busRoute.routeName;
              if (!acc[group]) {
                acc[group] = [];
              }
              acc[group].push(busRoute);
              return acc;
            },
            {} as Record<string, SegmentBusRoute[]>
          )
        ).map(([group, groupRoutes]) => {
          const allDeselected = groupRoutes.every((route) =>
            excludedRoutes.includes(route.routeId)
          );
          return (
            <VStack spacing={2} key={group} alignItems="stretch">
              <Box fontSize="sm" fontWeight="bold">
                {group}
              </Box>
              {groupRoutes.map((busRoute) => {
                const isSelected = !excludedRoutes.includes(busRoute.routeId);
                const firstStopId =
                  stopsOverride[busRoute.routeId]?.firstStopId ??
                  busRoute.firstStopId;
                const lastStopId =
                  stopsOverride[busRoute.routeId]?.lastStopId ??
                  busRoute.lastStopId;
                const hasSingleStopForRoute = firstStopId === lastStopId;
                return (
                  <VStack
                    spacing={1}
                    key={busRoute.routeId}
                    alignItems="stretch"
                  >
                    <HStack
                      spacing={2}
                      bg={
                        highlightedRoute === busRoute.routeId
                          ? 'gray.50'
                          : 'white'
                      }
                      onMouseEnter={() => onRouteHover(busRoute.routeId)}
                      onMouseLeave={() => onRouteHover(undefined)}
                      justifyContent="stretch"
                    >
                      <Flex
                        direction="column"
                        flex={1}
                        overflow="hidden"
                        alignItems="stretch"
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            if (excludedRoutes.includes(busRoute.routeId)) {
                              onSelectedRoutesChange(
                                excludedRoutes.filter(
                                  (id) => id !== busRoute.routeId
                                )
                              );
                            } else {
                              onSelectedRoutesChange([
                                ...excludedRoutes,
                                busRoute.routeId,
                              ]);
                            }
                          }}
                          color={routeColors[busRoute.routeId]}
                        >
                          <VStack spacing={0} alignItems="stretch" flex={1}>
                            <Flex gap={2}>
                              <Box
                                fontSize="xs"
                                fontWeight="bold"
                                textAlign="right"
                                flex="0 0 50px"
                              >
                                <FormattedMessage
                                  defaultMessage="From"
                                  id="dM+p3/"
                                />
                              </Box>
                              <Box
                                flex={1}
                                fontSize="sm"
                                overflow="hidden"
                                textOverflow="ellipsis"
                              >
                                {
                                  busRoute.stops.find(
                                    (s) =>
                                      s.stopId ===
                                      (stopsOverride[busRoute.routeId]
                                        ?.firstStopId ?? busRoute.firstStopId)
                                  )?.stopName
                                }
                              </Box>
                            </Flex>
                            <Flex gap={2}>
                              <Box
                                fontSize="xs"
                                fontWeight="bold"
                                textAlign="right"
                                flex="0 0 50px"
                              >
                                <FormattedMessage
                                  defaultMessage="to"
                                  id="NLeFGn"
                                />
                              </Box>
                              <Box
                                flex={1}
                                fontSize="sm"
                                overflow="hidden"
                                textOverflow="ellipsis"
                              >
                                {
                                  busRoute.stops.find(
                                    (s) =>
                                      s.stopId ===
                                      (stopsOverride[busRoute.routeId]
                                        ?.lastStopId ?? busRoute.lastStopId)
                                  )?.stopName
                                }
                              </Box>
                            </Flex>
                          </VStack>
                        </Checkbox>
                      </Flex>

                      <Box flex={0}>
                        <ContextMenu
                          size="xs"
                          triggerButtonProps={{
                            as: IconButton,
                            'aria-label': 'Options',
                            icon: <SettingsIcon />,
                            size: 'xs',
                            variant: 'ghost',
                            rightIcon: undefined,
                          }}
                          items={[
                            {
                              key: 'edit-stops',
                              label: (
                                <FormattedMessage
                                  defaultMessage="Edit stops"
                                  id="wkhR3y"
                                />
                              ),
                              action: () => {
                                setEditingRouteId(busRoute.routeId);
                              },
                            },
                          ]}
                        />
                      </Box>
                    </HStack>
                    {isSelected && hasSingleStopForRoute && (
                      <AlertBox
                        status="error"
                        message={
                          <FormattedMessage
                            defaultMessage="Only 1 stop selected."
                            id="Rd1sjZ"
                          />
                        }
                        cta={{
                          label: (
                            <Box fontWeight="normal" fontSize="xs" ml={1}>
                              <FormattedMessage
                                defaultMessage="Add another stop."
                                id="t5bMHK"
                              />
                            </Box>
                          ),
                          action: () => {
                            setEditingRouteId(busRoute.routeId);
                          },
                        }}
                      />
                    )}
                  </VStack>
                );
              })}
              {allDeselected && (
                <AlertBox status="warning" message="No stops selected." />
              )}
            </VStack>
          );
        })}
      </VStack>
      <BusStopsRouteEditorModal
        isOpen={editingRouteId !== undefined}
        onClose={() => {
          setEditingRouteId(undefined);
        }}
        onSave={(updatedBusRoute) => {
          onRouteStopsChange(updatedBusRoute);
          setEditingRouteId(undefined);
        }}
        busRoute={editingRoute}
      />
    </>
  );
}

function BusStopsRouteEditorModal({
  isOpen,
  busRoute,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (busRoute: SegmentBusRoute) => void;
  busRoute?: SegmentBusRoute;
}) {
  if (!busRoute) {
    return null;
  }
  return (
    <WizardModal isOpen={isOpen} onClose={onClose}>
      <BusStopsRouteEditor
        key={busRoute.routeId}
        busRoute={busRoute}
        onRouteSave={onSave}
        onCancel={onClose}
      />
    </WizardModal>
  );
}
